/**
 * RenewPage — Extend update entitlement by 1 year.
 * Protected route — only accessible when maintenance_end is in the past.
 */

import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { RefreshCw, ArrowLeft, Loader2, Calendar, CheckCircle } from 'lucide-react';
import { usePortal } from '../context/PortalContext';

function formatDate(isoStr) {
    if (!isoStr) return '—';
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(new Date(isoStr));
}

function addOneYear(isoStr) {
    const d = new Date(isoStr);
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().split('T')[0];
}

export function RenewPage() {
    const navigate = useNavigate();
    const { state, actions } = usePortal();
    const { license } = state;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // If maintenance is not expired, redirect to manage
    if (license && new Date(license.maintenanceEnd) >= new Date()) {
        return <Navigate to="/manage" replace />;
    }

    if (!license) {
        return <Navigate to="/lookup" replace />;
    }

    async function handleRenew() {
        setIsLoading(true);
        setError(null);
        try {
            await actions.renew();
            navigate('/manage', { replace: true });
        } catch (err) {
            setError(err.message || 'Renewal failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }

    const currentEnd = license.maintenanceEnd;
    const projectedEnd = addOneYear(currentEnd);

    return (
        <div className="animate-fade-in">
            <button
                type="button"
                onClick={() => navigate('/manage')}
                className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 mb-6 transition-colors"
            >
                <ArrowLeft className="w-4 h-4" />
                Back to license
            </button>

            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 space-y-6">
                <div className="text-center">
                    <RefreshCw className="w-10 h-10 mx-auto text-indigo-500 mb-3" />
                    <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                        Renew Update Entitlement
                    </h2>
                    <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 max-w-sm mx-auto">
                        Your Pro license is perpetual — it never expires. Renewing extends your
                        access to features released in the next year.
                    </p>
                </div>

                {/* Date comparison */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-zinc-50 dark:bg-zinc-800 rounded-lg p-4 text-center">
                        <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                            Current End Date
                        </p>
                        <div className="flex items-center justify-center gap-2 text-red-600 dark:text-red-400">
                            <Calendar className="w-4 h-4" />
                            <span className="font-semibold line-through">
                                {formatDate(currentEnd)}
                            </span>
                        </div>
                    </div>
                    <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-4 text-center">
                        <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-1">
                            New End Date
                        </p>
                        <div className="flex items-center justify-center gap-2 text-emerald-600 dark:text-emerald-400">
                            <CheckCircle className="w-4 h-4" />
                            <span className="font-semibold">{formatDate(projectedEnd)}</span>
                        </div>
                    </div>
                </div>

                {error && (
                    <p className="text-sm text-red-600 dark:text-red-400 text-center" role="alert">
                        {error}
                    </p>
                )}

                <button
                    type="button"
                    onClick={handleRenew}
                    disabled={isLoading}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <RefreshCw className="w-4 h-4" />
                    )}
                    {isLoading ? 'Renewing…' : 'Renew for 1 Year'}
                </button>
            </div>
        </div>
    );
}
